/* eslint-disable react-hooks/exhaustive-deps */
import YAML from "yaml";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { tokens } from "../../../theme";
import Header from "../../../components/Header";
import DataGridButton from "../../../components/DataGridButton";
import { Box, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { useListAllControlPlanes } from "../../../api/controlPlanes";
import { dataGridButtonTypography } from "../../../styles/DataGridStyles";
import { dataGridButtonStyles } from "../../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../../styles/commonPageStyles";
import { Info, Sync } from "@mui/icons-material";
import { Aws, Azure } from "../../../icons";
import { toTitleCase } from "../../../utils/helperFunctions";
import DataTable from "../../../components/DataTable";
import StatusChip from "../../../components/StatusChip";
import TimeCell from "../../../components/TimeCell";

const styles = {
  RmoTypography: { whiteSpace: "pre-wrap", fontFamily: "monospace" },
  // Column layout so the grid takes every pixel left under the header row.
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  dataTable: { flex: 1, minHeight: 0 },
};

const ControlPlanes = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const { deploymentStage } = useParams();

  const [controlPlanes, setControlPlanes] = useState([]);

  const {
    postData: listAllControlPlanes,
    response: listAllControlPlanesResp,
    isLoading: isLoadingControlPlanes,
  } = useListAllControlPlanes(deploymentStage);

  useEffect(() => {
    listAllControlPlanes();
  }, [deploymentStage]);

  useEffect(() => {
    if (Array.isArray(listAllControlPlanesResp?.controlPlanes)) {
      setControlPlanes(listAllControlPlanesResp.controlPlanes);
    } else {
      setControlPlanes([]);
    }
  }, [listAllControlPlanesResp]);

  const columns = [
    {
      field: "name",
      renderHeader: () => "Name",
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { name } }) => name || "",
    },
    {
      field: "releaseLabel",
      flex: 1,
      renderHeader: () => "Release Label",
      renderCell: ({ row: { releaseLabel, releaseManifestOverride } }) => (
        <>
          {releaseLabel ?? ""}
          {releaseManifestOverride ? (
            <Tooltip
              title={
                <Typography variant="body2" sx={styles.RmoTypography}>
                  {YAML.stringify(
                    { override: releaseManifestOverride },
                    null,
                    2
                  )}
                </Typography>
              }
            >
              <Info fontSize="small" />
            </Tooltip>
          ) : (
            <></>
          )}
        </>
      ),
    },
    {
      field: "k8sClusterName",
      flex: 2,
      renderHeader: () => "K8s Cluster",
      renderCell: ({ row: { k8sClusterName } }) => k8sClusterName ?? "",
    },
    {
      field: "cloud",
      flex: 1,
      renderHeader: () => "Cloud",
      renderCell: ({ row: { cloud } }) => {
        switch (cloud) {
          case "AWS":
            return <Aws size="16px" />;
          case "AZURE":
            return <Azure size="16px" />;
          default:
            return cloud ?? "";
        }
      },
    },
    {
      field: "region",
      flex: 1,
      renderHeader: () => "Region",
      renderCell: ({ row: { region } }) => region ?? "",
    },
    {
      field: "deploymentStatus",
      flex: 1,
      renderHeader: () => "Status",
      renderCell: ({ row: { deploymentStatus } }) => (
        <StatusChip value={deploymentStatus} />
      ),
    },
    {
      field: "dateModified",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Date Modified",
      renderCell: ({ row: { dateModified } }) => (
        <TimeCell value={dateModified} />
      ),
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid xs={1}>
          <Header
            title={`${toTitleCase(deploymentStage || "")} Control Planes`}
            subtitle={`See the list ${deploymentStage} control planes`}
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => listAllControlPlanes()}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>
      {/* Stage and prod are the same route with a different :deploymentStage,
          so router keeps this component mounted across the switch and the grid
          would carry its filters over with it. Keying on the stage rebuilds the
          grid instead, so each stage starts on a clean filter/sort state. */}
      <DataTable
        key={deploymentStage}
        hideFooter
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => row?.name}
        loading={isLoadingControlPlanes}
        rows={controlPlanes}
        columns={columns}
      />
    </Box>
  );
};

export default ControlPlanes;
